import { FC, useEffect, useState, useRef, ReactNode } from "react";

import { Table, Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";

import { ICase } from "store/cases/cases.types";
// import { getSeverityTitle } from "services/title.service";
import { useAppDispatch, useAppSelector } from "store";
import { getUserSelector } from "store/user/user.selector";
import { getCasesSelector } from "store/cases/cases.selector";
import { Loading } from "components/common";
import { getCases } from "store/cases/cases.thunks";
import { updateCasesSort } from "store/cases/cases.actions";
import { formatDateForApi } from "services/data.service";
import { RouteLinks } from "services/router.service";
import CompanyItem from "components/common/CompanyItem";

import "./CasesTable.scss";

const TruncatedTooltip: FC<{ title: string; children: ReactNode }> = ({ title, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [ isTruncated, setIsTruncated ] = useState(false);

  const handleMouseEnter = () => {
    if (ref.current) {
      const firstChild = ref.current.firstChild as HTMLElement;
      if (firstChild) {
        const isOverflowing = firstChild.scrollWidth > firstChild.clientWidth;
        setIsTruncated(isOverflowing);
      }
    }
  };

  return (
    <div
      ref={ ref }
      onMouseEnter={ handleMouseEnter }
      style={ { overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" } }
    >
      { isTruncated ? (
        <Tooltip title={ title }>
          { children }
        </Tooltip>
      ) : (
        children
      ) }
    </div>
  );
};

const CasesTable: FC = () => {
  const { isAdmin } = useAppSelector(getUserSelector);
  const { cases, isCasesLoading, sort } = useAppSelector(getCasesSelector);
  const dispatch = useAppDispatch();

  const [ updateState, setUpdateState ] = useState(false);
  useEffect(() => setUpdateState(!updateState), [ isCasesLoading ])

  const columns: ColumnsType<ICase> = [
    {
      title: "Case",
      dataIndex: "custom_id",
      key: "custom_id",
      render: (value, item) => <p className={ item.status === "open" ? "active" : "" }>{ value }</p>,
      sortOrder: sort.column === "custom_id" ? sort.direction : undefined,
      sorter: sort.column === "custom_id",
      showSorterTooltip: false,
      width: 170,
      onHeaderCell: () => ({
        id: "cases-header-cell",
        onClick: () => {
          dispatch(updateCasesSort({
            column: "custom_id",
            direction: sort.column === "custom_id" ? sort.direction === "ascend" ? "descend" : "ascend" : "ascend",
          }))
          dispatch(getCases())
        },
      }),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      className: "subject-cell",
      width: "25%",
      render: (value, item) => (
        <TruncatedTooltip title={ value }>
          <Link
            to={ `${ RouteLinks.PROTECTED.CASES.CASE }/${ item.id }` }
            rel="noopener noreferrer"
            className={ item.status === "open" ? "active" : "" }
          >
            { value }
          </Link>
        </TruncatedTooltip>
      ),
      sortOrder: sort.column === "subject" ? sort.direction : undefined,
      sorter: sort.column === "subject",
      showSorterTooltip: false,
      onHeaderCell: () => ({
        onClick: () => {
          dispatch(updateCasesSort({
            column: "subject",
            direction: sort.column === "subject" ? sort.direction === "ascend" ? "descend" : "ascend" : "ascend",
          }))
          dispatch(getCases())
        },
      }),
      onCell: () => ({
        className: "table-cell-limited",
      }),
    },
    {
      title: "Equipment",
      dataIndex: "equipment",
      key: "equipment",
      width: "25%",
      className: "equipment-cell",
      render: (_, item) => {
        const equipmentDescription = item.equipment?.product?.description || "—";
        return (
          <TruncatedTooltip title={ equipmentDescription }>
            <span className={ item.status === "open" ? "active" : "" }>
              { equipmentDescription }
            </span>
          </TruncatedTooltip>
        );
      },
      onCell: () => ({
        className: "table-cell-limited",
      }),
    },
    {
      title: "Opening Date",
      dataIndex: "created_at",
      key: "created_at",
      sortOrder: sort.column === "created_at" ? sort.direction : undefined,
      sorter: sort.column === "created_at",
      showSorterTooltip: false,
      width: 140,
      render: (value) => formatDateForApi(value),
      onHeaderCell: () => ({
        onClick: () => {
          dispatch(updateCasesSort({
            column: "created_at",
            direction: sort.column === "created_at" ? sort.direction === "ascend" ? "descend" : "ascend" : "ascend",
          }))
          dispatch(getCases())
        },
      }),
      onCell: () => ({
        style: { minWidth: "140px" },
      }),
    },
    // {
    //   title: "Severity",
    //   dataIndex: "severity",
    //   key: "severity",
    //   render: (value, item) => (
    //     <p
    //       className={ `severity ${ item.status === "open" ? "active" : "" } ${ value }` }
    //     >
    //       { getSeverityTitle(value) }
    //     </p>
    //   ),
    //   sortOrder: sort.column === "severity" ? sort.direction : undefined,
    //   sorter: sort.column === "severity",
    //   width: 130,
    //   showSorterTooltip: false,
    //   onHeaderCell: () => ({
    //     onClick: () => {
    //       dispatch(updateCasesSort({
    //         column: "severity",
    //         direction: sort.column === "severity" ? sort.direction === "ascend" ? "descend" : "ascend" : "ascend",
    //       }))
    //       dispatch(getCases())
    //     },
    //   }),
    // },
    {
      title: "Support Type",
      dataIndex: "support_type",
      key: "support_type",
      render: (_, item) => (
        item.sla_ci ? (
          <Tooltip title={ item.sla_ci?.name }>
            <div style={ { display: "flex" } }>
              <Tag
                style={ {
                  background: item.sla_ci.marker_bg_color,
                  color: item.sla_ci.marker_text_color,
                } }
              >
                { item.sla_ci?.marker }
              </Tag>
            </div>
          </Tooltip>
        ) : <div style={ { textAlign: "center", width: "70px" } }>{ "-//-" }</div>
      ),
      sortOrder: sort.column === "support_type" ? sort.direction : undefined,
      sorter: sort.column === "support_type",
      showSorterTooltip: false,
      width: 150,
      onHeaderCell: () => ({
        onClick: () => {
          dispatch(updateCasesSort({
            column: "support_type",
            direction: sort.column === "support_type" ? sort.direction === "ascend" ? "descend" : "ascend" : "ascend",
          }))
          dispatch(getCases())
        },
      }),
    },
    {
      title: "Closing Date",
      dataIndex: "closed_at",
      key: "closed_at",
      render: (value) => <p className={ value === null ? "align-center" : "" }>{ value === null ? "—" : formatDateForApi(value) }</p>,
      sortOrder: sort.column === "closed_at" ? sort.direction : undefined,
      sorter: sort.column === "closed_at",
      showSorterTooltip: false,
      width: 140,
      onHeaderCell: () => ({
        onClick: () => {
          dispatch(updateCasesSort({
            column: "closed_at",
            direction: sort.column === "closed_at" ? sort.direction === "ascend" ? "descend" : "ascend" : "ascend",
          }))
          dispatch(getCases())
        },
      }),
      onCell: () => ({
        style: { minWidth: "140px" },
      }),
    },
    {
      title: "Organisation",
      dataIndex: "customer",
      key: "customer",
      sortOrder: sort.column === "customer" ? sort.direction : undefined,
      sorter: sort.column === "customer",
      showSorterTooltip: false,
      width: 120,
      className: `customer-table-cell`,
      render: (_, item) => item.contractor ? (
        <CompanyItem
          email={ item.contractor.default_email }
          has_account={ item.contractor.has_account }
          has_verified_account={ item.contractor.has_verified_account }
          official_name={ item.contractor.official_name }
          phone={ item.contractor.phone_number }
          value={ item.contractor.name }
        />
      ) : "-/-",
      onHeaderCell: () => ({
        onClick: () => {
          dispatch(updateCasesSort({
            column: "customer",
            direction: sort.column === "customer" ? sort.direction === "ascend" ? "descend" : "ascend" : "ascend",
          }))
          dispatch(getCases())
        },
      }),
    },
  ]

  return (
    <Loading isLoading={ isCasesLoading } isColored height={ 200 }>
      <Table
        className="cases-table"
        columns={ columns }
        dataSource={ cases }
        rowKey={ (item) => item.id }
        pagination={ false }
        scroll={ { y: window.innerHeight - 164 - (document.getElementById("cases-header-cell")?.offsetHeight || 0) - (document.getElementById("cases-filter")?.offsetHeight || 0) } }
        tableLayout="auto"
      />
    </Loading>
  )
}

export default CasesTable;
